import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import BrandMark from '../components/BrandMark';

const OperationsGuide = ({ setActiveView }) => {
  const steps = [
    { title: 'Initialize Mission', desc: 'Define your launch goals, date, and core squad.', icon: 'Flag' },
    { title: 'Map Dependency', desc: 'Connect tasks across PR, Product, and Marketing teams.', icon: 'GitMerge' },
    { title: 'Payload Audit', desc: 'Review and approve creative assets in the Asset Room.', icon: 'Search' },
    { title: 'Relay Setup', desc: 'Configure automated socials and email comms relays.', icon: 'Radio' },
    { title: 'Sync Stakeholders', desc: 'Generate real-time executive summaries automatically.', icon: 'Share2' },
    { title: 'Pre-Flight Check', desc: 'Clear the Risk Radar of any high-level obstructions.', icon: 'ShieldCheck' },
    { title: 'Ignition', desc: 'Authorize the final stage gate and go live.', icon: 'Zap' },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-16 pb-20 px-4">
      <section className="text-center space-y-6">
        <div className="flex justify-center mb-4">
          <BrandMark className="w-16 h-16 animate-pulse-slow" />
        </div>
        <div className="inline-block px-3 py-1 bg-rocket-500/10 border border-rocket-500/30 rounded-full text-[10px] font-mono text-rocket-500 uppercase tracking-[0.2em]">
          Operational Protocol
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-ice tracking-tight">The Launch Command OS</h2>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
          LaunchLayer is the final "Source of Truth" for product teams shipping complex updates. We synchronize the gap between engineering readiness and marketing momentum.
        </p>
      </section>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="glass-panel p-8 rounded-2xl border border-slate-800 relative overflow-hidden group">
          <div className="absolute -top-4 -right-4 opacity-5 group-hover:opacity-10 transition-opacity">
            <SafeIcon name="AlertTriangle" size={120} />
          </div>
          <h3 className="text-xl font-bold text-ice mb-4 flex items-center">
            <SafeIcon name="XCircle" className="mr-2 text-red-500" /> The Problem
          </h3>
          <p className="text-slate-400 text-sm leading-relaxed">
            Launches fail because of **Comms Drift**. Engineering hits a snag, but Marketing has already spent the budget on a Tuesday launch. Stakeholders are left guessing until it's too late.
          </p>
        </div>
        <div className="glass-panel p-8 rounded-2xl border border-rocket-500/30 bg-rocket-500/5 relative overflow-hidden group">
          <div className="absolute -top-4 -right-4 opacity-5 group-hover:opacity-10 transition-opacity">
            <SafeIcon name="Zap" size={120} />
          </div>
          <h3 className="text-xl font-bold text-rocket-400 mb-4 flex items-center">
            <SafeIcon name="CheckCircle" className="mr-2" /> The Solution
          </h3>
          <p className="text-slate-400 text-sm leading-relaxed">
            LaunchLayer enforces **Stage Gate Authorization**. No campaign goes live until the technical payload is verified. One dashboard, absolute alignment, zero drift.
          </p>
        </div>
      </div>

      <section className="space-y-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-mono text-slate-500 uppercase tracking-widest italic">Mission Sequence (Step-by-Step)</h3>
            <p className="text-xs text-slate-600 mt-1">FOLLOW PROTOCOL FOR 100% READINESS</p>
          </div>
          <button 
            onClick={() => setActiveView('mission')}
            className="w-full md:w-auto px-6 py-2 bg-rocket-600 hover:bg-rocket-500 text-white rounded font-bold text-xs shadow-lg transition-all"
          >
            INITIALIZE FLOW
          </button>
        </div>

        <div className="grid gap-4">
          {steps.map((step, i) => (
            <motion.div 
              initial={{ opacity: 0, x: -15 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              key={i} 
              className="flex items-center p-5 glass-panel rounded-xl border border-slate-800 hover:border-rocket-500/40 transition-all group"
            >
              <div className="w-12 h-12 shrink-0 rounded-lg bg-slate-900 flex items-center justify-center text-rocket-500 border border-slate-800 group-hover:bg-rocket-600 group-hover:text-white transition-all shadow-inner">
                <SafeIcon name={step.icon} size={20} />
              </div>
              <div className="ml-6 flex-1 min-w-0">
                <h4 className="text-sm font-bold text-ice group-hover:text-rocket-400 transition-colors">{step.title}</h4>
                <p className="text-xs text-slate-500 mt-1 truncate">{step.desc}</p>
              </div>
              <div className="ml-4 text-[12px] font-mono text-slate-700 group-hover:text-rocket-500/50 transition-colors">0{i+1}</div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default OperationsGuide;